import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import AppHeader from '../../../components/AppHeader.tsx';
import QuickActions from '../../../components/QuickActions';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBell,
  faCalendarCheck,
  faCheckCircle,
  faClock,
  faExclamationTriangle,
  faSpinner,
  faTimes,
} from '@fortawesome/free-solid-svg-icons';
import { bookingsApi } from '@api/api';
import { BookingDto, BookingDtoStateEnum, BookingSummaryDto } from '@api/generated';

const TodayPage: React.FC = () => {
  const { entityId } = useParams<{ entityId: string }>();
  const [summary, setSummary] = useState<BookingSummaryDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const breadcrumb = [{ label: 'Dashboard' }, { label: 'Dnešní rezervace' }];

  useEffect(() => {
    if (!entityId) return;
    const today = dayjs().format('YYYY-MM-DD');
    setLoading(true);
    bookingsApi
      .getBookingSummary({ salonId: entityId, dateFrom: today, dateTo: today })
      .then(res => setSummary(res.data))
      .catch(err => setError(err?.message ?? 'Chyba načítání'))
      .finally(() => setLoading(false));
  }, [entityId]);

  const stateCounts = summary?.bookingsByState ?? {};
  const totalBookings = summary?.totalBookings ?? 0;
  const completed = stateCounts[BookingDtoStateEnum.Completed] ?? 0;
  const cancelled = stateCounts[BookingDtoStateEnum.Canceled] ?? 0;
  const pending = stateCounts[BookingDtoStateEnum.Pending] ?? 0;

  const todayStats = [
    { title: 'Dnešní rezervace', value: totalBookings.toString(), icon: faCalendarCheck, color: 'bg-green-50', iconColor: 'text-green-600' },
    { title: 'Dokončené', value: completed.toString(), icon: faCheckCircle, color: 'bg-blue-50', iconColor: 'text-blue-600' },
    { title: 'Zrušené', value: cancelled.toString(), icon: faTimes, color: 'bg-red-50', iconColor: 'text-red-600' },
    { title: 'Čekající', value: pending.toString(), icon: faClock, color: 'bg-yellow-50', iconColor: 'text-yellow-600' },
  ];

  const upcomingAppointments = useMemo(() => {
    const now = dayjs();
    return [...(summary?.bookings ?? [])]
      .filter(b => dayjs(b.start).isAfter(now) || (dayjs(b.start).isBefore(now) && dayjs(b.end).isAfter(now)))
      .sort((a, b) => dayjs(a.start).valueOf() - dayjs(b.start).valueOf())
      .slice(0, 8);
  }, [summary]);

  const alerts = useMemo(() => {
    const a: { type: 'warning' | 'info' | 'success'; message: string }[] = [];
    if (pending > 0) a.push({ type: 'warning', message: `${pending} rezervací čeká na potvrzení` });
    if (summary?.waitingCustomers && summary.waitingCustomers > 0) {
      a.push({ type: 'info', message: `${summary.waitingCustomers} zákazníků na čekací listině` });
    }
    if (summary?.newCustomers && summary.newCustomers > 0) {
      a.push({ type: 'success', message: `${summary.newCustomers} nových zákazníků dnes` });
    }
    return a;
  }, [summary, pending]);

  return (
    <div className="h-full flex flex-col">
      <AppHeader title="Dnešní rezervace" breadcrumb={breadcrumb} />

      <div className="flex-1 overflow-y-auto bg-gray-50">
        <div className="p-6 space-y-6">
          {error && <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-lg">{error}</div>}

          <section className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {todayStats.map((stat, index) => (
              <div key={index} className="bg-white rounded-lg shadow-sm p-6">
                <div className="flex items-center">
                  <div className={`p-3 rounded-lg ${stat.color}`}>
                    <FontAwesomeIcon icon={stat.icon} className={`text-xl ${stat.iconColor}`} />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">{stat.title}</p>
                    <div className="flex items-center">
                      {loading && <FontAwesomeIcon icon={faSpinner} className="animate-spin text-gray-400 mr-2" />}
                      <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </section>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 bg-white rounded-lg shadow-sm">
              <div className="p-6 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">Nadcházející rezervace</h3>
              </div>
              <div className="p-6">
                {upcomingAppointments.length === 0 ? (
                  <p className="text-sm text-gray-500">Žádné další rezervace dnes.</p>
                ) : (
                  <div className="space-y-4">
                    {upcomingAppointments.map(appointment => (
                      <UpcomingItem key={appointment.id} booking={appointment} />
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm">
              <div className="p-6 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">Upozornění</h3>
              </div>
              <div className="p-6">
                {alerts.length === 0 ? (
                  <p className="text-sm text-gray-500">Vše v pořádku.</p>
                ) : (
                  <div className="space-y-4">
                    {alerts.map((alert, index) => (
                      <div key={index} className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg">
                        <FontAwesomeIcon
                          icon={alert.type === 'warning' ? faExclamationTriangle : alert.type === 'info' ? faBell : faCheckCircle}
                          className={`w-4 h-4 mt-1 ${
                            alert.type === 'warning' ? 'text-yellow-600' : alert.type === 'info' ? 'text-blue-600' : 'text-green-600'
                          }`}
                        />
                        <p className="text-sm text-gray-900">{alert.message}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          <section className="bg-white rounded-lg shadow-sm p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Rychlé akce</h3>
            {entityId && <QuickActions salonId={entityId} />}
          </section>
        </div>
      </div>
    </div>
  );
};

const UpcomingItem: React.FC<{ booking: BookingDto }> = ({ booking }) => {
  const time = dayjs(booking.start).format('HH:mm');
  const durationMin = dayjs(booking.end).diff(dayjs(booking.start), 'minute');
  const customerName = `${booking.customer.firstName} ${booking.customer.lastName}`.trim();
  const serviceName = booking.bookingLines?.[0]?.serviceName ?? '';
  const isInProgress = dayjs().isAfter(booking.start) && dayjs().isBefore(booking.end);
  return (
    <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
      <div className="flex items-center space-x-4">
        <div className="text-center">
          <p className="text-lg font-bold text-gray-900">{time}</p>
          <p className="text-xs text-gray-500">{durationMin} min</p>
        </div>
        <div className="flex-1">
          <p className="font-medium text-gray-900">{customerName}</p>
          <p className="text-sm text-gray-600">{serviceName}</p>
          <p className="text-xs text-gray-500">{booking.worker.displayName}</p>
        </div>
      </div>
      <div className="flex items-center space-x-2">
        <span className={`px-2 py-1 text-xs rounded-full ${isInProgress ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'}`}>
          {isInProgress ? 'Probíhá' : 'Čeká'}
        </span>
        <button className="p-2 text-gray-400 hover:text-gray-600">
          <FontAwesomeIcon icon={faBell} className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

export default TodayPage;
